/**
 * Centralized enum/value → configuration for the Badge component (`color`, i18n keys, etc.).
 * Add new exported helpers here as reusable badge mappings are needed.
 */
import { DEFAULT_EMPTY_VALUE } from '@/utils/constants';
import { CoreEnums } from '@/utils/domain/constants';

const { InternalTaskStatus, OperationResultStatus } = CoreEnums;

export function getOperationResultStatusProcessBadgeStatus(status) {
  if (status == null || status === '') return 'default';
  const resolved =
    typeof status === 'string' && status.trim() !== '' && !Number.isNaN(Number(status))
      ? Number(status)
      : status;
  if (typeof resolved === 'number' && !Number.isFinite(resolved)) return 'default';
  const byStatus = {
    [OperationResultStatus.Pending]: 'pending',
    [OperationResultStatus.Executing]: 'processing',
    [OperationResultStatus.Success]: 'approved',
    [OperationResultStatus.Error]: 'rejected',
    [OperationResultStatus.Failed]: 'rejected',
  };
  return byStatus[resolved] ?? 'default';
}

const INTERNAL_TASK_STATUS_BADGE_CONFIG = {
  [InternalTaskStatus.Pending]: {
    color: 'pending',
    statusText: 'internalTaskStatus.pending',
  },
  [InternalTaskStatus.Processing]: {
    color: 'info',
    statusText: 'internalTaskStatus.processing',
  },
  [InternalTaskStatus.Processed]: {
    color: 'success',
    statusText: 'internalTaskStatus.processed',
  },
  [InternalTaskStatus.Error]: {
    color: 'error',
    statusText: 'internalTaskStatus.error',
  },
  [InternalTaskStatus.Skipped]: {
    color: 'default',
    statusText: 'internalTaskStatus.skipped',
  },
  [InternalTaskStatus.Cancelled]: {
    color: 'canceled',
    statusText: 'internalTaskStatus.cancelled',
  },
  [InternalTaskStatus.Failed]: {
    color: 'failed',
    statusText: 'internalTaskStatus.failed',
  },
  [InternalTaskStatus.Paused]: {
    color: 'premiumTier',
    statusText: 'internalTaskStatus.paused',
  },
};

/** For OData filter selects (`badgeMap` on EnumFilter config). */
export const internalTaskStatusBadgeMap = Object.fromEntries(
  Object.entries(INTERNAL_TASK_STATUS_BADGE_CONFIG).map(([key, { color }]) => [
    Number(key),
    { status: color },
  ]),
);

/** Maps InternalTaskStatus to Badge `color` + i18n id for `statusText`. */
export const getInternalTaskStatusBadgeConfig = (status) => {
  if (status === null || status === undefined) {
    return {
      value: DEFAULT_EMPTY_VALUE,
      color: 'default',
      statusText: 'internalTaskStatus.unknown',
    };
  }

  const resolved =
    typeof status === 'string' && status.trim() !== '' && !Number.isNaN(Number(status))
      ? Number(status)
      : status;

  return (
    INTERNAL_TASK_STATUS_BADGE_CONFIG[resolved] ||
    INTERNAL_TASK_STATUS_BADGE_CONFIG[status] || {
      color: 'default',
      statusText: 'internalTaskStatus.unknown',
    }
  );
};
